import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { BookUser, ChevronDown, ChevronUp, CreditCard, DollarSign, Gift, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CounterPromoCodeBlock } from '@/components/counter/CounterPromoCodeBlock'
import { CounterCardPromoBlock } from '@/components/counter/CounterCardPromoBlock'

export type TenderIntent = 'cash' | 'card' | 'online'

const intentMeta = {
  cash: {
    label: 'Cash',
    Icon: DollarSign,
    active:
      'border-emerald-600 bg-emerald-600 text-white shadow-sm shadow-emerald-900/20 ring-1 ring-emerald-500/30',
    idle:
      'border-emerald-200 bg-emerald-50/80 text-emerald-900 hover:border-emerald-300 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-50 dark:hover:bg-emerald-900/50',
  },
  card: {
    label: 'Card',
    Icon: CreditCard,
    active:
      'border-sky-600 bg-sky-600 text-white shadow-sm shadow-sky-900/20 ring-1 ring-sky-400/35',
    idle:
      'border-sky-200 bg-sky-50/80 text-sky-950 hover:border-sky-300 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50 dark:hover:bg-sky-900/50',
  },
  online: {
    label: 'Online',
    Icon: Globe,
    active:
      'border-violet-600 bg-violet-600 text-white shadow-sm shadow-violet-900/25 ring-1 ring-violet-400/35',
    idle:
      'border-violet-200 bg-violet-50/85 text-violet-950 hover:border-violet-300 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:bg-violet-900/50',
  },
} as const

export interface TenderInputProps {
  intent: TenderIntent
  onIntent: (i: TenderIntent) => void
  /**
   * F-MM-N23: when set, only this tender can be chosen; the others render
   * disabled with `lockedHint` underneath. Marketplace-settled orders are
   * locked to Online — the platform pays, not the till.
   */
  lockedIntent?: TenderIntent
  lockedHint?: string
  discountMode: 'amount' | 'percent'
  onDiscountModeChange: (mode: 'amount' | 'percent') => void
  discountValue: string
  onDiscountValueChange: (v: string) => void
  discountPending: boolean
  onApplyDiscount: () => void
  /** Order id used by the inline promo redemption block. */
  orderId: string
  /** Forwarded to the promo block — totals shift on apply/remove. */
  onPromoChanged?: () => void
  /**
   * Defaults the discount/promo accordion to open when the order already
   * carries a manual discount or an applied promo (both write to
   * `discount_amount` server-side, so one flag covers both).
   */
  hasExistingDiscount?: boolean
  /**
   * F-MM-N24: opens the gift-card dialog. Passed only when the tenant has the
   * tender enabled, the bill still owes something, and the order is not
   * marketplace-settled — the parent resolves all three, so the presence of
   * this handler is the single signal to render the row.
   *
   * It lives inside the adjustments panel rather than beside the tender tiles
   * on purpose: those tiles choose the order's TAX INTENT, and a gift card
   * carries no rate of its own. Putting it there would imply a fourth rate.
   */
  onApplyGiftCard?: () => void
  /**
   * F-MM-N30: opens the charge-to-account dialog (staff meal / customer tab).
   * Passed only when the whole bill is still open — the parent resolves that,
   * so the presence of the handler is the signal to render the row.
   */
  onChargeToAccount?: () => void
}

export function TenderInput({
  intent,
  onIntent,
  lockedIntent,
  lockedHint,
  discountMode,
  onDiscountModeChange,
  discountValue,
  onDiscountValueChange,
  discountPending,
  onApplyDiscount,
  orderId,
  onPromoChanged,
  hasExistingDiscount,
  onApplyGiftCard,
  onChargeToAccount,
}: TenderInputProps) {
  const [discountOpen, setDiscountOpen] = useState<boolean>(Boolean(hasExistingDiscount))
  return (
    <section
      aria-labelledby="tender-input-heading"
      className="space-y-3 rounded-lg border border-border bg-card/50 p-3"
    >
      {/* Everything that CHANGES what is owed comes first — discount, promo
          code, gift card — and the tender tiles follow, because picking how
          the guest pays is the last decision at the till. */}
      <div>
        <button
          type="button"
          onClick={() => setDiscountOpen((v) => !v)}
          aria-expanded={discountOpen}
          aria-controls="counter-discount-panel"
          className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background/40 px-3 py-2 text-left transition-colors hover:bg-muted/40"
        >
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {onApplyGiftCard ? 'Discount & gift card' : 'Discount'}
          </span>
          {discountOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
        </button>
        {discountOpen && (
          <div id="counter-discount-panel" className="mt-3 space-y-3">
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Discount (whole bill)
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="grid shrink-0 grid-cols-2 gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={discountMode === 'amount' ? 'default' : 'outline'}
                    className="h-9 px-2 text-sm"
                    onClick={() => onDiscountModeChange('amount')}
                    aria-pressed={discountMode === 'amount'}
                  >
                    Rs
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={discountMode === 'percent' ? 'default' : 'outline'}
                    className="h-9 px-2 text-sm"
                    onClick={() => onDiscountModeChange('percent')}
                    aria-pressed={discountMode === 'percent'}
                  >
                    %
                  </Button>
                </div>
                <Input
                  inputMode="decimal"
                  className="h-9 min-w-[6rem] flex-1 text-sm"
                  placeholder={discountMode === 'percent' ? 'e.g. 10' : '0.00'}
                  value={discountValue}
                  onChange={(e) => onDiscountValueChange(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="h-9 shrink-0 px-3 text-sm"
                  disabled={discountPending}
                  onClick={onApplyDiscount}
                >
                  Apply
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Enter 0 and apply to clear discount.</p>
            </div>

            <CounterPromoCodeBlock orderId={orderId} onChanged={onPromoChanged} />

            {/* F-MM-N24. A gift card is NOT a discount — it is the guest's own
                money settling part of the bill — so it gets its own labelled
                row rather than being folded in beside the percentage box. */}
            {onApplyGiftCard ? (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Gift card
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-9 w-full border-dashed text-sm font-medium"
                  onClick={onApplyGiftCard}
                >
                  <Gift className="mr-2 h-4 w-4 shrink-0" aria-hidden />
                  Apply gift card
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  Takes the balance off this bill. Anything left is settled with the method below.
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div>
        <h3
          id="tender-input-heading"
          className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Method
        </h3>
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {(['cash', 'card', 'online'] as const).map((key) => {
            const meta = intentMeta[key]
            const Icon = meta.Icon
            const on = intent === key
            const locked = lockedIntent != null && lockedIntent !== key
            return (
              <button
                key={key}
                type="button"
                onClick={() => { if (!locked) onIntent(key) }}
                disabled={locked}
                title={locked ? lockedHint : undefined}
                className={cn(
                  'flex h-11 flex-col items-center justify-center gap-0 rounded-lg border px-1 text-[11px] font-semibold uppercase tracking-wide transition-colors sm:h-12 sm:text-xs',
                  on ? meta.active : meta.idle,
                  locked && 'cursor-not-allowed opacity-40'
                )}
                aria-pressed={on}
                aria-disabled={locked || undefined}
              >
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                {meta.label}
              </button>
            )
          })}
        </div>
        {lockedIntent && lockedHint ? (
          <p className="mt-1.5 text-[11px] text-muted-foreground">{lockedHint}</p>
        ) : null}
        {/* Bank card promo quick-pick — directly under the tender buttons
            (not inside the collapsed Discount panel): the cashier taps
            Card, then taps the matching promo chip. Visible only on the
            card intent; the backend enforces the same condition. */}
        <div className="mt-2 empty:mt-0">
          <CounterCardPromoBlock
            orderId={orderId}
            visible={intent === 'card'}
            onChanged={onPromoChanged}
          />
        </div>
        {onChargeToAccount ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2 h-9 w-full border-dashed text-sm font-medium"
            onClick={onChargeToAccount}
          >
            <BookUser className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            Charge to account
          </Button>
        ) : null}
      </div>
    </section>
  )
}
